/**
 * ApprovalCardSection component.
 *
 * Wraps each renderer with:
 * - Section `label` as the heading.
 * - `collapsed` initial state respected; user can expand/collapse manually.
 * - If `section.error` is populated, render an inline error.
 * - If `section.data` is `null`/`undefined`, render "No data provided."
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { ChevronDown, ChevronRight, AlertTriangle, Maximize2 } from 'lucide-react'
import { renderByName } from './renderers/registry'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import type { ApprovalCardSection as ApprovalCardSectionType } from '../../types'

export interface ApprovalCardSectionProps {
  /** The section configuration from the card payload. */
  section: ApprovalCardSectionType
  /** Optional callback when collapse state changes. */
  onToggle?: (id: string, collapsed: boolean) => void
  /** Override for collapse state (controlled mode). */
  isCollapsed?: boolean
}

export function ApprovalCardSection({
  section,
  onToggle,
  isCollapsed: controlledCollapsed,
}: ApprovalCardSectionProps) {
  const [internalCollapsed, setInternalCollapsed] = React.useState(section.collapsed)
  // Whether this section is popped out into the full-size modal. Generic
  // across every tool — large evidence payloads (e.g. dispute snapshots)
  // are hard to read inside the narrow card column, so any section can be
  // expanded into a wide scrollable dialog that re-renders the SAME content
  // via the SAME registry renderer at full width.
  const [expanded, setExpanded] = React.useState(false)

  const isControlled = controlledCollapsed !== undefined
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed

  const handleToggle = React.useCallback(() => {
    const newState = !collapsed
    if (!isControlled) {
      setInternalCollapsed(newState)
    }
    onToggle?.(section.id, newState)
  }, [collapsed, isControlled, onToggle, section.id])

  return (
    <section
      style={styles.section}
      aria-labelledby={`section-heading-${section.id}`}
    >
      {/* Section header row: the collapse toggle (chevron + heading) takes
          the full remaining width as a single comfortable hover target,
          with a quiet "Expand" affordance pinned to the right so it never
          competes with the heading. */}
      <div style={styles.headerRow}>
        <button
          type="button"
          onClick={handleToggle}
          style={styles.toggleButton}
          className="approval-section-toggle"
          aria-expanded={!collapsed}
          aria-controls={`section-content-${section.id}`}
        >
          {collapsed ? (
            <ChevronRight
              style={styles.chevron}
              aria-hidden="true"
            />
          ) : (
            <ChevronDown
              style={styles.chevron}
              aria-hidden="true"
            />
          )}
          <h3
            id={`section-heading-${section.id}`}
            style={styles.sectionHeading}
          >
            {section.label}
          </h3>
        </button>

        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={styles.expandButton}
          className="approval-section-expand"
          aria-label={`Expand ${section.label}`}
          title="Expand"
        >
          <Maximize2 style={styles.expandIcon} aria-hidden="true" />
        </button>
      </div>

      {/* Section content */}
      <div
        id={`section-content-${section.id}`}
        style={{
          ...styles.sectionContent,
          ...(collapsed ? { display: 'none' } : {}),
        }}
      >
        <SectionContent section={section} />
      </div>

      {/* Full-size expansion modal. Renders the identical section content
          through the same registry renderer — no truncation, no 68ch prose
          cap — inside a wide scrollable dialog. */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          style={styles.expandDialogContent}
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle>{section.label}</DialogTitle>
          </DialogHeader>
          <div style={styles.expandDialogBody}>
            <SectionContent section={section} />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}

interface SectionContentProps {
  section: ApprovalCardSectionType
}

function SectionContent({ section }: SectionContentProps) {
  // If `section.error` is populated, render an inline error
  if (section.error) {
    return <SectionError error={section.error} data={section.data} />
  }

  // If `section.data` is `null`/`undefined`, render "No data provided."
  if (section.data === null || section.data === undefined) {
    return (
      <p style={styles.noDataText}>
        No data provided.
      </p>
    )
  }

  // Render using the appropriate renderer
  return renderByName(section.renderer, section.data)
}

interface SectionErrorProps {
  error: string
  data: unknown
}

/**
 * Inline error display.
 *
 * Shows the error message with a "Show raw data" toggle.
 */
function SectionError({ error, data }: SectionErrorProps) {
  const [showRaw, setShowRaw] = React.useState(false)

  return (
    <div
      role="alert"
      style={styles.errorContainer}
    >
      <div style={styles.errorHeader}>
        <AlertTriangle style={styles.errorIcon} />
        <div style={styles.errorContent}>
          <p style={styles.errorMessage}>
            Unable to render this section: {error}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowRaw((prev) => !prev)}
        style={styles.showRawButton}
        className="link-hover"
        aria-expanded={showRaw}
      >
        {showRaw ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRaw && (
        <pre style={styles.rawDataPre}>
          {safeStringify(data)}
        </pre>
      )}
    </div>
  )
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const styles: Record<string, CSSProperties> = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  toggleButton: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: '6px',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 6px 2px 0',
    margin: '0 0 0 -6px',
    borderRadius: 'var(--radius-sm)',
    transition: 'background-color var(--transition-fast)',
  },
  chevron: {
    width: '14px',
    height: '14px',
    color: 'var(--text-secondary, var(--text-muted))',
    flexShrink: 0,
  },
  expandButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-muted)',
    opacity: 0.7,
    transition: 'opacity var(--transition-fast)',
  },
  expandIcon: {
    width: '14px',
    height: '14px',
  },
  expandDialogContent: {
    width: '92vw',
    maxWidth: '1100px',
    maxHeight: '85vh',
    overflow: 'hidden',
  },
  expandDialogBody: {
    overflow: 'auto',
    maxHeight: 'calc(85vh - 80px)',
    wordBreak: 'break-word',
    paddingRight: '4px',
  },
  // Top-level section heading. Larger and sentence-cased so it visibly
  // outranks the sub-block labels rendered inside execution-summary
  // content (which keep the small ALL-CAPS treatment). The previous
  // 11px uppercase styling collided with sub-labels and produced a
  // flat hierarchy where everything looked equally important.
  sectionHeading: {
    fontSize: '13px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '-0.005em',
    color: 'var(--text-primary)',
    margin: 0,
  },
  sectionContent: {
    // Indent matches chevron + chevron-gap, giving a clean optical
    // alignment with the heading text rather than the chevron.
    paddingLeft: '20px',
    overflow: 'hidden',
    wordBreak: 'break-word' as const,
    // No maxWidth here: prose blocks (markdown, multiline) cap themselves
    // at 68ch via their own styles so they stay readable in wide panels
    // without constraining structured/nested content that benefits from
    // filling the available column width.
  },
  noDataText: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    background: 'rgba(239, 68, 68, 0.05)',
    padding: '12px',
  },
  errorHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  errorIcon: {
    marginTop: '2px',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    color: 'var(--error)',
  },
  errorContent: {
    flex: 1,
    fontSize: '14px',
  },
  errorMessage: {
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  showRawButton: {
    alignSelf: 'flex-start',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--accent-primary)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  rawDataPre: {
    maxHeight: '240px',
    overflow: 'auto',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-tertiary)',
    padding: '8px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
}

export default ApprovalCardSection
